import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParameterCodec, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { Injectable } from '@angular/core';
import { catchError, finalize, takeUntil } from 'rxjs/operators';

import { ACCESS_TOKEN, TOKEN_EXCLUDE_URLS, TOKEN_TYPE } from '@constants/setting-keys.const';
import { CacheService } from './cache.service';
import { RequestLoaderService } from './request-loader.service';
import { ApiErrorService } from './api-error.service';
import { HttpMethod } from '@enum/http-method';

class CustomEncoder implements HttpParameterCodec {
  public encodeKey(key: string): string {
    return encodeURIComponent(key);
  }

  public encodeValue(value: string): string {
    return encodeURIComponent(value);
  }

  public decodeKey(key: string): string {
    return decodeURIComponent(key);
  }

  public decodeValue(value: string): string {
    return decodeURIComponent(value);
  }
}

interface HttpObjOptions {
  headers?: Object;
  params?: Object;
  responseType?: string;
  observe?: string;
}

interface HttpRequestObjOptions {
  body?: any;
  headers?:
    | HttpHeaders
    | {
        [header: string]: string | string[];
      };
  observe?: 'body';
  params?:
    | HttpParams
    | {
        [param: string]: string | string[];
      };
  responseType?: 'json';
  reportProgress?: boolean;
  withCredentials?: boolean;
}

@Injectable()
export class ApiService {
  // public readonly url: string = environment.baseUrl;
  public cancelRequests$: ReplaySubject<void> = new ReplaySubject<void>();

  private pendingQueries$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  private pendingFetchQueries$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);

  constructor(
    private http: HttpClient,
    private cacheService: CacheService,
    private requestLoaderService: RequestLoaderService,
    private apiErrorService: ApiErrorService,
  ) {}

  public get<T>(
    path: string,
    options?: HttpObjOptions,
    cache?: { isUsed?: boolean; maxAge?: number; isShowLoader?: boolean },
    customErrorHandlerFunction?: (err: HttpErrorResponse) => Observable<any>,
  ): Observable<T> {
    const isShowLoader = !!cache ? (cache.isShowLoader !== undefined ? cache.isShowLoader : true) : true;
    if (isShowLoader) {
      this.requestLoaderService.showAPILoader();
    }

    const uniqId: string = this.uniqStr();
    this.pendingFetchQueries$.next([...this.pendingFetchQueries$.getValue(), uniqId]);

    const key: string = JSON.stringify({ path: path, options });
    const httpGet = this.http.get<T>(this.buildPath(path), this.buildOptions(path, options)).pipe(
      takeUntil(this.cancelRequests$),
      finalize(() => {
        this.pendingFetchQueries$.next(this.pendingFetchQueries$.getValue().filter((item: string) => item !== uniqId));
        this.requestLoaderService.hideAPILoader();
      }),
      catchError((err) => {
        return !!customErrorHandlerFunction ? customErrorHandlerFunction(err) : this.apiErrorService.handleError(path, HttpMethod.GET, err);
      }),
    );

    if (cache && cache.isUsed) {
      return this.cacheService.get(key, httpGet).pipe(
        takeUntil(this.cancelRequests$),
        finalize(() => {
          this.pendingFetchQueries$.next(this.pendingFetchQueries$.getValue().filter((item: string) => item !== uniqId));
          this.requestLoaderService.hideAPILoader();
        }),
      );
    }

    return httpGet;
  }

  public post<T>(
    path: string,
    body?: Object,
    options?: HttpObjOptions,
    cache?: { isShowLoader?: boolean },
    customErrorHandlerFunction?: (err: HttpErrorResponse) => Observable<any>,
  ): Observable<T> {
    const isShowLoader = !!cache ? (cache.isShowLoader !== undefined ? cache.isShowLoader : true) : true;
    if (isShowLoader) {
      this.requestLoaderService.showAPILoader();
    }

    const uniqId = this.uniqStr();
    this.pendingQueries$.next([...this.pendingQueries$.getValue(), uniqId]);

    return this.http.post<T>(this.buildPath(path), body, this.buildOptions(path, options)).pipe(
      takeUntil(this.cancelRequests$),
      finalize(() => {
        this.pendingQueries$.next(this.pendingQueries$.getValue().filter((item: string) => item !== uniqId));
        this.requestLoaderService.hideAPILoader();
      }),
      catchError((err) => {
        return !!customErrorHandlerFunction
          ? customErrorHandlerFunction(err)
          : this.apiErrorService.handleError(path, HttpMethod.POST, err);
      }),
    );
  }

  public put<T>(
    path: string,
    body?: Object,
    options?: HttpObjOptions,
    cache?: { isShowLoader?: boolean },
    customErrorHandlerFunction?: (err: HttpErrorResponse) => Observable<any>,
  ): Observable<T> {
    const isShowLoader = !!cache ? (cache.isShowLoader !== undefined ? cache.isShowLoader : true) : true;
    if (isShowLoader) {
      this.requestLoaderService.showAPILoader();
    }

    const uniqId = this.uniqStr();
    this.pendingQueries$.next([...this.pendingQueries$.getValue(), uniqId]);

    return this.http.put<T>(this.buildPath(path), body, this.buildOptions(path, options)).pipe(
      takeUntil(this.cancelRequests$),
      finalize(() => {
        this.pendingQueries$.next(this.pendingQueries$.getValue().filter((item: string) => item !== uniqId));
        this.requestLoaderService.hideAPILoader();
      }),
      catchError((err) => {
        return !!customErrorHandlerFunction ? customErrorHandlerFunction(err) : this.apiErrorService.handleError(path, HttpMethod.PUT, err);
      }),
    );
  }

  public patch<T>(path: string, body?: Object, options?: HttpObjOptions, cache?: { isShowLoader?: boolean; asd?: boolean }): Observable<T> {
    const isShowLoader = !!cache ? (cache.isShowLoader !== undefined ? cache.isShowLoader : true) : true;
    if (isShowLoader) {
      this.requestLoaderService.showAPILoader();
    }

    const uniqId = this.uniqStr();
    this.pendingQueries$.next([...this.pendingQueries$.getValue(), uniqId]);

    return this.http.patch<T>(this.buildPath(path), body, this.buildOptions(path, options)).pipe(
      takeUntil(this.cancelRequests$),
      finalize(() => {
        this.pendingQueries$.next(this.pendingQueries$.getValue().filter((item: string) => item !== uniqId));
        this.requestLoaderService.hideAPILoader();
      }),
      catchError((err) => {
        return this.apiErrorService.handleError(path, HttpMethod.PATCH, err);
      }),
    );
  }

  public delete<T>(path: string, options?: HttpObjOptions, cache?: { isShowLoader?: boolean }): Observable<T> {
    const isShowLoader = !!cache ? (cache.isShowLoader !== undefined ? cache.isShowLoader : true) : true;
    if (isShowLoader) {
      this.requestLoaderService.showAPILoader();
    }

    const uniqId = this.uniqStr();
    this.pendingQueries$.next([...this.pendingQueries$.getValue(), uniqId]);

    return this.http.delete<T>(this.buildPath(path), this.buildOptions(path, options)).pipe(
      takeUntil(this.cancelRequests$),
      finalize(() => {
        this.pendingQueries$.next(this.pendingQueries$.getValue().filter((item: string) => item !== uniqId));
        this.requestLoaderService.hideAPILoader();
      }),
      catchError((err) => {
        return this.apiErrorService.handleError(path, HttpMethod.DELETE, err);
      }),
    );
  }

  public request<T>(method: string, path: string, options?: HttpRequestObjOptions, cache?: { isShowLoader?: boolean }): Observable<T> {
    const isShowLoader = !!cache ? (cache.isShowLoader !== undefined ? cache.isShowLoader : true) : true;
    if (isShowLoader) {
      this.requestLoaderService.showAPILoader();
    }

    return this.http
      .request<T>(method, this.buildPath(path), { ...options, ...this.buildOptions(path, options) } as HttpRequestObjOptions)
      .pipe(
        takeUntil(this.cancelRequests$),
        finalize(() => {
          this.requestLoaderService.hideAPILoader();
        }),
      );
  }

  public destroyRequests(): void {
    this.cancelRequests$.next();
    this.cancelRequests$.complete();
    this.cancelRequests$ = new ReplaySubject<void>();
  }

  public get allPendingQueries(): string[] {
    return [...this.pendingQueries$.getValue(), ...this.pendingFetchQueries$.getValue()];
  }

  public get isAnyPendingQueries(): boolean {
    return !!this.pendingQueries$.getValue().length;
  }

  public get isAnyPendingQueriesWithFetch(): boolean {
    return !!this.allPendingQueries.length;
  }

  private buildOptions(path: string, options?: HttpObjOptions) {
    // #12930: 'ngsw-bypass' makes requests skip the Angular service worker. Without it ngsw keeps its own
    // network fetch alive even after the app cancels the request (e.g. on navigation via cancelRequests$),
    // so the backend keeps processing work nobody needs. With the bypass the browser abort reaches the server,
    // letting it stop processing (backend CancellationToken support).
    // NOTE: requests that pass custom `options.headers` replace this whole object and do NOT get the bypass —
    // add the header there explicitly if cancellation matters for that endpoint.
    const headers = (options && options.headers) || {
      'Content-Type': 'application/json',
      'ngsw-bypass': 'true',
      Authorization: TOKEN_EXCLUDE_URLS.some((url) => path.includes(url))
        ? ''
        : `${localStorage.getItem(TOKEN_TYPE)} ${localStorage.getItem(ACCESS_TOKEN)}` || '',
    };

    const params = (options && options.params) || {};
    const responseType: any = options?.responseType || undefined;
    const observe: any = options?.observe || undefined;

    return {
      headers: this.toHttpHeaders(headers),
      params: new HttpParams({ fromObject: params as any, encoder: new CustomEncoder() }), // converted to HttpParams & fromObject,
      responseType,
      observe,
    };
  }

  private toHttpHeaders(headers?: Object): HttpHeaders {
    return Object.getOwnPropertyNames(headers).reduce((p, key) => p.set(key, headers[key]), new HttpHeaders());
  }

  private toHttpParams(params?: Object): HttpParams {
    return Object.getOwnPropertyNames(params).reduce((p, key) => p.set(key, params[key]), new HttpParams({ encoder: new CustomEncoder() }));
  }

  private buildPath(path: string) {
    return `${path}`;
  }

  private uniqStr(): string {
    return Date.now().toString(36) + Math.random().toString(36).substring(2);
  }
}
